import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Bell, Check, CheckCheck, Trash2, ExternalLink, Info, AlertTriangle, AlertCircle, CheckCircle, FileText, GitBranch, LayoutGrid, Users, ShoppingCart, Calendar } from 'lucide-react';
import { Button, Popover, PopoverContent, PopoverTrigger } from '@caruso/ui';
import { notificationsService, Notification, NotificationType } from '@/services/notifications.service';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';

const typeConfig: Record<NotificationType, { icon: any; color: string; bgColor: string }> = {
  [NotificationType.INFO]: { icon: Info, color: 'text-blue-400', bgColor: 'bg-blue-400/10' },
  [NotificationType.WARNING]: { icon: AlertTriangle, color: 'text-yellow-400', bgColor: 'bg-yellow-400/10' },
  [NotificationType.ERROR]: { icon: AlertCircle, color: 'text-red-400', bgColor: 'bg-red-400/10' },
  [NotificationType.SUCCESS]: { icon: CheckCircle, color: 'text-green-400', bgColor: 'bg-green-400/10' },
  [NotificationType.TASK]: { icon: FileText, color: 'text-purple-400', bgColor: 'bg-purple-400/10' },
  [NotificationType.FLOW]: { icon: GitBranch, color: 'text-cyan-400', bgColor: 'bg-cyan-400/10' },
  [NotificationType.BOARD]: { icon: LayoutGrid, color: 'text-indigo-400', bgColor: 'bg-indigo-400/10' },
  [NotificationType.CLIENT]: { icon: Users, color: 'text-emerald-400', bgColor: 'bg-emerald-400/10' },
  [NotificationType.ORDER]: { icon: ShoppingCart, color: 'text-orange-400', bgColor: 'bg-orange-400/10' },
  [NotificationType.RESERVATION]: { icon: Calendar, color: 'text-pink-400', bgColor: 'bg-pink-400/10' },
};

export function NotificationDropdown() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);

  const { data: countData } = useQuery({
    queryKey: ['notifications-count'],
    queryFn: () => notificationsService.getUnreadCount(),
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  const { data: notificationsData, isLoading } = useQuery({
    queryKey: ['notifications', { limit: 10 }],
    queryFn: () => notificationsService.getAll({ limit: 10 }),
    enabled: isOpen,
  });

  const markAsReadMutation = useMutation({
    mutationFn: (ids: string[]) => notificationsService.markAsRead(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-count'] });
    },
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: () => notificationsService.markAllAsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-count'] });
    },
  });

  const unreadCount = countData?.count || 0;
  const notifications = notificationsData?.data || [];

  const handleNotificationClick = (notification: Notification) => {
    // Marcar como lida
    if (!notification.isRead && !notification.read) {
      markAsReadMutation.mutate([notification.id]);
    }

    // Navegar se tiver link
    if (notification.link) {
      setIsOpen(false);
      navigate(notification.link);
    }
  };

  const formatTime = (date: string) => {
    return formatDistanceToNow(new Date(date), { addSuffix: true, locale: ptBR });
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-9 w-9"
        >
          <Bell className="h-5 w-5 text-brand-ivory/80" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-96 p-0 bg-brand-dark-lighter border-brand-gold/20"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-brand-gold/20">
          <h3 className="font-semibold text-white">Notificacoes</h3>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => markAllAsReadMutation.mutate()}
              disabled={markAllAsReadMutation.isPending}
              className="text-xs text-brand-gold hover:bg-brand-gold/10"
            >
              <CheckCheck className="h-4 w-4 mr-1" />
              Marcar todas como lidas
            </Button>
          )}
        </div>

        <div className="max-h-96 overflow-y-auto">
          {isLoading ? (
            <div className="p-4 text-center text-brand-ivory/60">
              Carregando...
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-8 text-center">
              <Bell className="h-12 w-12 mx-auto text-brand-ivory/30 mb-3" />
              <p className="text-brand-ivory/60">Nenhuma notificacao</p>
            </div>
          ) : (
            <div className="divide-y divide-brand-gold/10">
              {notifications.map((notification) => {
                const isRead = notification.isRead || notification.read;
                const config = typeConfig[notification.type] || typeConfig[NotificationType.INFO];
                const Icon = config.icon;

                return (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`px-4 py-3 cursor-pointer transition-colors hover:bg-brand-gold/5 ${
                      !isRead ? 'bg-brand-gold/5' : ''
                    }`}
                  >
                    <div className="flex gap-3">
                      <div className={`flex-shrink-0 w-8 h-8 rounded-full ${config.bgColor} flex items-center justify-center`}>
                        <Icon className={`h-4 w-4 ${config.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-sm font-medium truncate ${isRead ? 'text-brand-ivory/70' : 'text-white'}`}>
                            {notification.title}
                          </p>
                          {!isRead && (
                            <span className="flex-shrink-0 w-2 h-2 rounded-full bg-brand-gold" />
                          )}
                        </div>
                        <p className={`text-xs mt-0.5 line-clamp-2 ${isRead ? 'text-brand-ivory/50' : 'text-brand-ivory/70'}`}>
                          {notification.message}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-[10px] text-brand-ivory/40">
                            {formatTime(notification.createdAt)}
                          </span>
                          {notification.link && (
                            <ExternalLink className="h-3 w-3 text-brand-ivory/40" />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {notifications.length > 0 && (
          <div className="px-4 py-3 border-t border-brand-gold/20">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setIsOpen(false);
                navigate('/app/notificacoes');
              }}
              className="w-full text-brand-gold hover:bg-brand-gold/10"
            >
              Ver todas as notificacoes
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
